import { ClockCounterClockwise } from '@phosphor-icons/react';
import { Dispatch, SetStateAction, useRef, useState, useEffect } from 'react';
import { Conversation } from '../../../../@types/IA.type';
import { useStorage } from '../../../../shared/utils/general.util';
import STORAGE_KEYS from '../../../../shared/constants/storage';
import { RootState } from '../../../../redux/store';
import { useSelector } from 'react-redux';

const ChatHistory = ({
	chatHistory,
	setCurrentThread,
	getMessageHistory,
}: {
	chatHistory: Conversation[];
	setCurrentThread: Dispatch<SetStateAction<Conversation>>;
	getMessageHistory: (userId: number, refresh?: boolean) => Promise<void>;
	currentThread: Conversation;
}) => {
	const [showHistory, setShowHistory] = useState(false);
	const historyRef = useRef<HTMLDivElement>(null);
	const { user: userProfile } = useSelector(
		(state: RootState) => state.profile,
	);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				historyRef.current &&
				!historyRef.current.contains(event.target as Node)
			) {
				setShowHistory(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	const handleClickThread = async (conversation: Conversation) => {
		setCurrentThread(conversation);
		setShowHistory(false);
		useStorage().setItem(STORAGE_KEYS.LAST_CHAT_AI, conversation.id.toString());
	};

	const isHistoryEmpty = chatHistory?.length === 0;
	const chatHistorySorted =
		chatHistory?.length > 0
			? chatHistory?.sort((a, b) => {
					return (
						new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
					);
				})
			: [];

	const handleShowHistory = async () => {
		setShowHistory(!showHistory);
		await getMessageHistory(userProfile?.id!, false);
	};

	return (
		<div
			className='relative w-full'
			ref={historyRef}
		>
			{!isHistoryEmpty && (
				<div
					className={`z-10 flex flex-col absolute top-10 -left-20 md:left-0 w-[240px] border-h-neutral-90  bg-white/90 backdrop-blur-xl rounded-lg transition-[opacity,transform] duration-250 ${
						showHistory
							? 'max-h-[300px] overflow-y-auto p-2 shadow border translate-y-0 opacity-100 scale-100'
							: 'max-h-0 overflow-hidden translate-y-3 opacity-0 scale-[0.96]'
					}`}
				>
					{chatHistorySorted.map((thread, index) => (
						<div>
							<button
								key={index}
								onClick={() => handleClickThread(thread)}
								className='text-left hover:bg-h-neutral-94 p-1 rounded-lg font-light leading-tigh w-full'
							>
								<div className='text-[10px] opacity-50 -mb-1 text-h-neutral-500 flex items-center gap-1'>
									<p>
										{new Date(thread.updated_at).toLocaleDateString('es-ES', {
											day: '2-digit',
											month: '2-digit',
											year: 'numeric',
										})}
									</p>
									<div className='size-0.5 bg-h-neutral-70 rounded-full' />
									<p>
										{new Date(thread.updated_at).toLocaleTimeString('es-ES', {
											hour: '2-digit',
											minute: '2-digit',
										})}
									</p>
									<div className='size-0.5 bg-h-neutral-70 rounded-full' />
									<p>{thread.thread.length} Mensajes</p>
								</div>
								<p className='truncate text-sm'>{thread.thread[1].content}</p>
							</button>
							<hr className='opacity-50' />
						</div>
					))}
				</div>
			)}
			<button
				disabled={chatHistory.length === 0}
				onClick={handleShowHistory}
				className='flex items-center gap-1 hover:text-h-neutral-20 text-h-neutral-40 text-xs w-fit bg-h-neutral-94 hover:bg-h-primary-90/50 pr-2 rounded-3xl p-1 disabled:opacity-50 disabled:cursor-not-allowed'
			>
				<ClockCounterClockwise
					size={18}
					weight='bold'
				/>{' '}
				Historial
			</button>
		</div>
	);
};

export default ChatHistory;
